import { getSupabaseClient } from "../../providers/supabase/supabase";
import type { MapPoint } from "./directoryMapData";

/**
 * Ronda abierta en `prospecting_rounds` / `prospecting_round_stops` (PLAN_RUTAS.md §B):
 * sobrevive a cerrar el navegador. Nunca guarda la ubicación del vendedor, solo las
 * paradas (ids del directorio), su orden y las visitas.
 *
 * Tolerante a la migración 20261002010000 sin aplicar: si la columna `position` no
 * existe, guardar/recuperar no hacen nada y la ruta funciona solo en memoria, como antes.
 */

export type RoundOrigin = {
  filter?: Record<string, unknown>;
  size?: number;
  seen?: number[];
};

export type StopRow = {
  round_id: number;
  directory_id: number;
  position: number;
  visited_at: string | null;
};

export type RestoredRound = {
  stops: MapPoint[];
  visited: number[];
  origen: RoundOrigin;
};

type DirectoryLite = {
  id: number;
  lat: number | null;
  lng: number | null;
  nombre_comercial: string | null;
  razon_social: string | null;
  thermo_base: number | null;
  company_id: number | null;
};

/** Filas de paradas en el orden mostrado; las visitadas llevan `visited_at`. */
export const buildStopRows = (
  roundId: number,
  ids: number[],
  visited: ReadonlySet<number>,
  now: string,
): StopRow[] =>
  ids.map((directory_id, position) => ({
    round_id: roundId,
    directory_id,
    position,
    visited_at: visited.has(directory_id) ? now : null,
  }));

/** Une las paradas guardadas con su fila del directorio, en el orden guardado. */
export const restoreStops = (
  rows: Pick<StopRow, "directory_id" | "position" | "visited_at">[],
  directory: DirectoryLite[],
): Pick<RestoredRound, "stops" | "visited"> => {
  const byId = new Map(directory.map((d) => [d.id, d]));
  const ordered = [...rows]
    .sort((a, b) => a.position - b.position)
    .filter((r) => byId.has(r.directory_id));
  return {
    stops: ordered.map((r) => {
      const d = byId.get(r.directory_id)!;
      return {
        id: d.id,
        lat: d.lat,
        lng: d.lng,
        nombre: d.nombre_comercial ?? d.razon_social ?? `Empresa ${d.id}`,
        thermo_base: d.thermo_base ?? 0,
        company_id: d.company_id,
      };
    }),
    visited: ordered.filter((r) => r.visited_at).map((r) => r.directory_id),
  };
};

let available: Promise<boolean> | null = null;

/** ¿Existe `prospecting_round_stops.position` (migración aplicada) y hay sesión? */
const isAvailable = (): Promise<boolean> => {
  available ??= Promise.resolve()
    .then(() =>
      getSupabaseClient()
        .from("prospecting_round_stops")
        .select("position")
        .limit(0),
    )
    .then(
      ({ error }) => !error,
      () => false,
    );
  return available;
};

const rpcNumber = async (fn: string): Promise<number> => {
  const { data, error } = await getSupabaseClient().rpc(fn);
  if (error || typeof data !== "number")
    throw new Error(error?.message ?? `${fn} sin valor`);
  return data;
};

const findOpenRound = async (salesId: number) => {
  const { data, error } = await getSupabaseClient()
    .from("prospecting_rounds")
    .select("id, origen")
    .eq("sales_id", salesId)
    .is("closed_at", null)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as { id: number; origen: RoundOrigin } | null;
};

/**
 * Guarda la ronda abierta del vendedor (crea una si no hay). Sin paradas, la cierra.
 * workspace_id va explícito (el de quien guarda), nunca por el default de la columna.
 * ponytail: borra y reescribe las paradas en cada cambio (no es atómico); basta para
 * rondas de ~10 paradas de un solo usuario.
 */
export const saveOpenRound = async (
  ids: number[],
  visited: ReadonlySet<number>,
  origen: RoundOrigin,
): Promise<void> => {
  if (!(await isAvailable())) return;
  const sb = getSupabaseClient();
  const salesId = await rpcNumber("current_sales_id");
  const open = await findOpenRound(salesId);

  if (ids.length === 0) {
    if (!open) return;
    const { error } = await sb
      .from("prospecting_rounds")
      .update({ closed_at: new Date().toISOString() })
      .eq("id", open.id);
    if (error) throw new Error(error.message);
    return;
  }

  let roundId = open?.id;
  if (roundId) {
    const { error } = await sb
      .from("prospecting_rounds")
      .update({ origen })
      .eq("id", roundId);
    if (error) throw new Error(error.message);
  } else {
    const workspaceId = await rpcNumber("default_workspace_id");
    const { data, error } = await sb
      .from("prospecting_rounds")
      .insert({ sales_id: salesId, workspace_id: workspaceId, origen })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    roundId = (data as { id: number }).id;
  }

  const del = await sb
    .from("prospecting_round_stops")
    .delete()
    .eq("round_id", roundId);
  if (del.error) throw new Error(del.error.message);
  const ins = await sb
    .from("prospecting_round_stops")
    .insert(buildStopRows(roundId, ids, visited, new Date().toISOString()));
  if (ins.error) throw new Error(ins.error.message);
};

/** ¿El vendedor tiene una ronda abierta guardada? false si no se puede saber. */
export const hasOpenRound = async (): Promise<boolean> => {
  if (!(await isAvailable())) return false;
  return (await findOpenRound(await rpcNumber("current_sales_id"))) !== null;
};

/** La ronda abierta del vendedor con sus paradas, o null si no hay (o no se puede). */
export const loadOpenRound = async (): Promise<RestoredRound | null> => {
  if (!(await isAvailable())) return null;
  const sb = getSupabaseClient();
  const open = await findOpenRound(await rpcNumber("current_sales_id"));
  if (!open) return null;
  const { data: rows, error } = await sb
    .from("prospecting_round_stops")
    .select("directory_id, position, visited_at")
    .eq("round_id", open.id);
  if (error) throw new Error(error.message);
  if (!rows?.length) return null;
  const { data: dir, error: err2 } = await sb
    .from("directory")
    .select(
      "id, lat, lng, nombre_comercial, razon_social, thermo_base, company_id",
    )
    .in(
      "id",
      rows.map((r) => r.directory_id),
    );
  if (err2) throw new Error(err2.message);
  return {
    ...restoreStops(rows as StopRow[], (dir ?? []) as DirectoryLite[]),
    origen: open.origen ?? {},
  };
};

/** Descarte permanente: saca la empresa de todas las rondas futuras (no es "no contactar"). */
export const discardDirectory = async (directoryId: number): Promise<void> => {
  const { error } = await getSupabaseClient().rpc("directory_discard", {
    p_directory_id: directoryId,
  });
  if (error)
    throw new Error(
      "No se pudo descartar. Si el problema sigue, falta aplicar la actualización de la base.",
    );
};
